import { divide } from "@aprao/common/src/math/divide.js";
import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import { sum } from "@aprao/common/src/math/sum.js";
import {
  formatDecimalNumber,
  formatPercentage,
} from "@aprao/common/src/numberFormats.js";
import {
  otherIncomeGroupPredicate,
  salesGroupPredicate,
} from "@aprao/model/src/shapes/ShapeRevenueGroup.js";
import type { AppraisalDocumentFacade } from "@aprao/model/src/stores/AppraisalDocumentFacade.js";
import type { OtherCostsRelativesStore } from "@aprao/model/src/stores/createOtherCostsRelatives.js";
import type { FinanceFacade } from "@aprao/model/src/stores/FinanceFacade.js";
import type { FlowFacade } from "@aprao/model/src/stores/FlowFacade.js";
import type { ResidualFacade } from "@aprao/model/src/stores/ResidualFacade.js";
import type { SalesTaxConfigurationFacade } from "@aprao/model/src/stores/SalesTaxConfigurationFacade.js";
import type { TaxFacade } from "@aprao/model/src/stores/TaxFacade.js";
import { observer } from "mobx-react-lite";
import { mapForNullable } from "option-t/nullable";

import { useI18n } from "../../common/ApraoI18n.js";
import { FinanceSummaryRelatives } from "../../common/FinanceSummaryRelatives.js";
import { useNumberUnits } from "../../common/useNumberUnits.js";
import { buildSalesTaxName } from "../../i18n/buildSalesTaxName.js";
import { Section } from "../Section.js";
import { SimpleTableRow, SimpleTableSection } from "../SimpleTableRow.js";

const Sales = (props: {
  appraisal: AppraisalDocumentFacade;
  salesTaxConfiguration: SalesTaxConfigurationFacade | null;
  tax: TaxFacade;
}) => {
  const { appraisal, salesTaxConfiguration, tax } = props;

  const { msg } = useI18n();
  const { lengthUnit, currency } = useNumberUnits();

  return (
    <SimpleTableSection>
      <SimpleTableRow
        isHeader
        cells={[
          "Revenue",
          "Units",
          `${lengthUnit.symbol}² (${msg("global.nia")})`,
          `${currency.symbol}/${lengthUnit.symbol}²`,
          "Ave Value",
          `Total (${currency.symbol})`,
        ]}
      />
      {appraisal.revenueGroups.some(salesGroupPredicate) && (
        <SimpleTableRow
          cells={[
            "Sales revenue",
            appraisal.salesUnitsCountTotal,
            formatDecimalNumber(appraisal.salesAreaTotal ?? FRACTION_ZERO),
            formatDecimalNumber(
              appraisal.averageSalesValuePerArea ?? FRACTION_ZERO,
            ),
            formatDecimalNumber(appraisal.averageSalesValue ?? FRACTION_ZERO),
            formatDecimalNumber(appraisal.salesGrossValue ?? FRACTION_ZERO),
          ]}
        />
      )}
      {appraisal.revenueGroups.some(otherIncomeGroupPredicate) && (
        <SimpleTableRow
          cells={[
            "Income",
            appraisal.incomeUnitsCountTotal,
            null,
            null,
            formatDecimalNumber(appraisal.averageIncomeValue ?? FRACTION_ZERO),
            formatDecimalNumber(appraisal.incomeGrossValue ?? FRACTION_ZERO),
          ]}
        />
      )}
      <SimpleTableRow
        bold
        borderBottom
        borderTop
        cells={[
          msg("global.grossDevelopmentValue"),
          null,
          null,
          null,
          null,
          formatDecimalNumber(
            appraisal.stages.revenue.getGrossDevelopmentValue("any") ??
              FRACTION_ZERO,
          ),
        ]}
      />
      <SimpleTableRow
        cells={[
          "Costs",
          null,
          null,
          null,
          null,
          formatDecimalNumber(appraisal.salesCostTotal ?? FRACTION_ZERO),
        ]}
      />
      {salesTaxConfiguration != null && (
        <SimpleTableRow
          cells={[
            buildSalesTaxName(salesTaxConfiguration.$model),
            null,
            null,
            null,
            null,
            formatDecimalNumber(tax.gdvSalesTaxOnAmount ?? FRACTION_ZERO),
          ]}
        />
      )}
      <SimpleTableRow
        bold
        borderTop
        borderBottom={!appraisal.hasAdditionalIncome}
        thickerBorder
        cells={[
          msg("global.netDevelopmentValue"),
          null,
          null,
          null,
          null,
          formatDecimalNumber(tax.netDevelopmentValue ?? FRACTION_ZERO),
        ]}
      />
      {appraisal.hasAdditionalIncome && (
        <>
          <SimpleTableRow
            bold
            borderTop
            thickerBorder
            cells={[
              "Additional income",
              null,
              null,
              null,
              null,
              formatDecimalNumber(
                appraisal.getAdditionalIncomeTotal("any") ?? FRACTION_ZERO,
              ),
            ]}
          />
          <SimpleTableRow
            bold
            borderTop
            borderBottom
            thickerBorder
            cells={[
              msg("global.netRevenue"),
              null,
              null,
              null,
              null,
              formatDecimalNumber(tax.netRevenue ?? FRACTION_ZERO),
            ]}
          />
        </>
      )}
    </SimpleTableSection>
  );
};

const SitePurchase = (props: { residual: ResidualFacade }) => {
  const { residual } = props;

  const { currency } = useNumberUnits();
  const { msg } = useI18n();

  return (
    <SimpleTableSection>
      <SimpleTableRow
        isHeader
        cells={[
          "Site Purchase",
          null,
          null,
          null,
          "of price",
          `Total (${currency.symbol})`,
        ]}
      />
      <SimpleTableRow
        cells={[
          "Site purchase price",
          null,
          null,
          null,
          null,
          formatDecimalNumber(
            residual.sitePurchasesPriceTotal ?? FRACTION_ZERO,
          ),
        ]}
      />
      <SimpleTableRow
        cells={[
          msg("global.landTax"),
          null,
          null,
          null,
          formatPercentage(residual.landTaxesTotalRatio ?? FRACTION_ZERO),
          formatDecimalNumber(residual.landTaxesTotal ?? FRACTION_ZERO),
        ]}
      />
      <SimpleTableRow
        cells={[
          "Other purchase costs",
          null,
          null,
          null,
          formatPercentage(
            residual.sitePurchasesCustomCostsTotalRatio ?? FRACTION_ZERO,
          ),
          formatDecimalNumber(
            residual.sitePurchasesCustomCostsTotal ?? FRACTION_ZERO,
          ),
        ]}
      />
      <SimpleTableRow
        bold
        borderBottom
        borderTop
        cells={[
          "Total purchase costs",
          null,
          null,
          null,
          null,
          formatDecimalNumber(residual.sitePurchasesCostTotal ?? FRACTION_ZERO),
        ]}
      />
    </SimpleTableSection>
  );
};

const BuildCosts = (props: { appraisal: AppraisalDocumentFacade }) => {
  const { appraisal } = props;

  const { msg } = useI18n();
  const { lengthUnit, currency } = useNumberUnits();

  const totalContingency = sum(
    appraisal.buildCostsPhases.map(({ contingency }) =>
      contingency.relativesSet.getRelative("absolute"),
    ),
  );

  const costPerGrossArea = divide(
    appraisal.stages.build.buildCostMainTotal,
    appraisal.grossAreaTotal,
  );

  return (
    <SimpleTableSection>
      <SimpleTableRow
        isHeader
        cells={[
          msg("global.buildCosts"),
          null,
          null,
          `${lengthUnit.symbol}² (${msg("global.gia")})`,
          `${currency.symbol}/${lengthUnit.symbol}²`,
          `Total (${currency.symbol})`,
        ]}
        data-testid="sf__build-costs-row"
      />
      <SimpleTableRow
        cells={[
          "Preliminary costs",
          null,
          null,
          null,
          null,
          formatDecimalNumber(
            appraisal.buildCostsPreliminary.totalCost ?? FRACTION_ZERO,
          ),
        ]}
      />
      <SimpleTableRow
        cells={[
          msg("finance.mainBuildCosts"),
          null,
          null,
          formatDecimalNumber(appraisal.grossAreaTotal ?? FRACTION_ZERO),
          formatDecimalNumber(costPerGrossArea ?? FRACTION_ZERO),
          formatDecimalNumber(
            appraisal.stages.build.buildCostMainTotal ?? FRACTION_ZERO,
          ),
        ]}
      />
      <SimpleTableRow
        cells={[
          "Contingency",
          null,
          null,
          null,
          null,
          formatDecimalNumber(totalContingency ?? FRACTION_ZERO),
        ]}
      />
      <SimpleTableRow
        bold
        borderBottom
        borderTop
        cells={[
          msg("global.totalBuildCosts"),
          null,
          null,
          null,
          null,
          formatDecimalNumber(
            appraisal.getBuildCostTotalWithContingency("any") ?? FRACTION_ZERO,
          ),
        ]}
      />
    </SimpleTableSection>
  );
};

const OtherCosts = (props: {
  otherCostsRelatives: OtherCostsRelativesStore;
  appraisal: AppraisalDocumentFacade;
}) => {
  const { otherCostsRelatives, appraisal } = props;

  const { msg } = useI18n();
  const numberUnits = useNumberUnits();

  return (
    <SimpleTableSection>
      <SimpleTableRow
        isHeader
        cells={[
          msg("global.otherCosts"),
          null,
          null,
          msg("global.percentBuild"),
          msg("global.percentGdv"),
          `Total (${numberUnits.currency.symbol})`,
        ]}
        data-testid="sf__other-costs-row"
      />
      <SimpleTableRow
        borderBottom
        bold
        cells={[
          msg("global.totalOtherCosts"),
          null,
          null,
          formatPercentage(
            otherCostsRelatives.relativesSet.getRelative(
              "ratioBuildCostGlobalTotal",
            ) ?? FRACTION_ZERO,
          ),
          formatPercentage(
            otherCostsRelatives.relativesSet.getRelative("ratioGdv") ??
              FRACTION_ZERO,
          ),
          formatDecimalNumber(appraisal.otherCostsTotal ?? FRACTION_ZERO),
        ]}
      />
    </SimpleTableSection>
  );
};

const Finance = observer(function Finance(props: {
  appraisal: AppraisalDocumentFacade;
  residual: ResidualFacade;
  finance: FinanceFacade;
  flow: FlowFacade;
}) {
  const { appraisal, residual, finance, flow } = props;

  const { facilitySizeTotal } = new FinanceSummaryRelatives({
    appraisal: appraisal,
    residual: residual,
    finance: finance,
    flow: flow,
  });

  const { msg } = useI18n();
  const numberUnits = useNumberUnits();

  return (
    <SimpleTableSection>
      <SimpleTableRow
        isHeader
        cells={[
          msg("global.finance"),
          null,
          "% Cost (inc. finance)",
          "% Cost",
          msg("global.percentGdv"),
          `Total (${numberUnits.currency.symbol})`,
        ]}
      />
      <SimpleTableRow
        cells={[
          "Total finance",
          null,
          formatPercentage(
            facilitySizeTotal.relativesSet.getRelative(
              "ratioDebtFundedDevAndFinanceCost",
            ) ?? FRACTION_ZERO,
          ),
          formatPercentage(
            facilitySizeTotal.relativesSet.getRelative(
              "ratioDebtFundedDevCost",
            ) ?? FRACTION_ZERO,
          ),
          formatPercentage(
            facilitySizeTotal.relativesSet.getRelative("ratioDebtFundedGdv") ??
              FRACTION_ZERO,
          ),
          formatDecimalNumber(finance.facilitySizeTotal ?? FRACTION_ZERO),
        ]}
      />
      <SimpleTableRow
        bold
        borderBottom
        borderTop
        cells={[
          "Total finance costs",
          null,
          null,
          null,
          null,
          formatDecimalNumber(flow.financeCostsTotal ?? FRACTION_ZERO),
        ]}
      />
    </SimpleTableSection>
  );
});

export const SummaryFinancial = observer(function SummaryFinancial(props: {
  appraisal: AppraisalDocumentFacade;
  salesTaxConfiguration: SalesTaxConfigurationFacade | null;
  tax: TaxFacade;
  residual: ResidualFacade;
  finance: FinanceFacade;
  flow: FlowFacade;
  otherCostsRelatives: OtherCostsRelativesStore;
}) {
  const {
    appraisal,
    salesTaxConfiguration,
    tax,
    residual,
    finance,
    flow,
    otherCostsRelatives,
  } = props;

  const { msg } = useI18n();

  return (
    <Section title={msg("report.summaryFinancialAppraisal")}>
      <Sales
        appraisal={appraisal}
        salesTaxConfiguration={salesTaxConfiguration}
        tax={tax}
      />
      <SitePurchase residual={residual} />
      <BuildCosts appraisal={appraisal} />
      <OtherCosts
        otherCostsRelatives={otherCostsRelatives}
        appraisal={appraisal}
      />

      <SimpleTableSection>
        <SimpleTableRow
          bold
          borderBottom
          borderTop
          thickerBorder
          cells={[
            "Total costs excl finance",
            null,
            null,
            null,
            null,
            formatDecimalNumber(
              residual.getDevelopmentCostsTotal("any") ?? FRACTION_ZERO,
            ),
          ]}
        />
      </SimpleTableSection>

      <Finance
        appraisal={appraisal}
        residual={residual}
        finance={finance}
        flow={flow}
      />

      <SimpleTableSection>
        <SimpleTableRow
          bold
          borderBottom
          borderTop
          thickerBorder
          cells={[
            "Total costs inc. finance",
            null,
            null,
            null,
            null,
            formatDecimalNumber(
              flow.getDevelopmentAndFinanceCostsTotal("any") ?? FRACTION_ZERO,
            ),
          ]}
        />
        <SimpleTableRow
          cellCount={7}
          cells={[
            null,
            msg("global.projectIrr"),
            msg("global.equityIrr"),
            "RoE",
            msg("global.roc"),
            msg("global.roGdv"),
            null,
          ]}
        />
        <SimpleTableRow
          bold
          borderTop
          borderBottom
          thickerBorder
          cellCount={7}
          cells={[
            "Profit",
            mapForNullable(flow.projectIrr, formatPercentage) ?? "N/A",
            mapForNullable(flow.developerContributionIrr, formatPercentage) ??
              "N/A",
            formatPercentage(
              flow.returnOnDeveloperContribution ?? FRACTION_ZERO,
            ),
            formatPercentage(flow.returnOnCost ?? FRACTION_ZERO),
            formatPercentage(flow.returnOnGdv ?? FRACTION_ZERO),
            formatDecimalNumber(flow.developmentProfit ?? FRACTION_ZERO),
          ]}
        />
      </SimpleTableSection>
    </Section>
  );
});
